import { useId } from "react";
import { LOCATION_KINDS } from "@ondo/shared";
import type { WorkLocation, WorkState } from "@ondo/shared";

import { LOCATION_LABELS } from "../lib/constants.ts";
import { COUNTRY_OPTIONS, LOCATION_COPY } from "./location.constants.ts";
import { useLocationController } from "./useLocationController.ts";
import type { LocationController } from "./useLocationController.ts";
import "./location.css";

/**
 * Where the work is, and — for its owner — the one place to say so.
 *
 * Mounted on a work's record under the consignment panel. Every reader sees
 * the line; only the owner sees the question and the form, because
 * `ondo.set_location()` asks `owns_work` and a control the server would
 * refuse is worse than none.
 *
 * THE QUESTION IS THE POINT. Since 20260925100000 a sale clears the seller's
 * location, so the first thing a new owner's record does is ask "Where is it
 * now?" — the owner's own answer to the finding that a bought work carried
 * the seller's shelf. Two openers with different names from the submit, so a
 * screen reader hears one "Record location" at a time.
 */
export function LocationPanel({
  workId,
  initial,
  canAct,
  onRecorded,
}: {
  workId: string;
  initial: WorkState | null;
  canAct: boolean;
  onRecorded?: (location: WorkLocation) => void;
}) {
  const c = useLocationController({ workId, initial, canAct, onRecorded });
  const { view, form } = c;

  return (
    <section className="location">
      <header className="location__head">
        <h2 className="location__title">{LOCATION_COPY.title}</h2>
        {canAct && !form.open && (
          <button type="button" className="location__action" onClick={c.openForm}>
            {view.kind === "recorded" ? LOCATION_COPY.update : LOCATION_COPY.record}
          </button>
        )}
      </header>

      {view.kind === "recorded" && (
        <>
          <p className="location__line">{view.line}</p>
          {view.region && (
            <p className="location__region">
              <span className="location__region-label">{LOCATION_COPY.region}</span>
              {view.region}
            </p>
          )}
        </>
      )}

      {view.kind === "none" && !form.open && (
        <>
          <p className={view.prompt ? "location__ask" : "location__line"}>{view.message}</p>
          {view.detail && <p className="location__note">{view.detail}</p>}
        </>
      )}

      {form.saved && !form.open && (
        <p className="location__sent" role="status">
          {LOCATION_COPY.recorded}
        </p>
      )}

      {form.open && <LocationForm c={c} />}

      {c.error && (
        <p className="location__note location__note--error" role="alert">
          {c.error}
        </p>
      )}
    </section>
  );
}

function LocationForm({ c }: { c: LocationController }) {
  const { draft, saving } = c.form;
  const copy = LOCATION_COPY.form;
  const kindId = useId();
  const placeId = useId();
  const countryId = useId();
  const cityId = useId();

  return (
    <form
      className="location__form"
      onSubmit={(event) => {
        event.preventDefault();
        c.save();
      }}
    >
      <h3 className="location__form-title">{copy.title}</h3>

      <label className="location__label" htmlFor={kindId}>
        {copy.kind}
      </label>
      <select
        id={kindId}
        className="location__input"
        value={draft.kind}
        onChange={(event) => c.setDraft({ kind: event.target.value as typeof draft.kind })}
        disabled={saving}
      >
        {LOCATION_KINDS.map((each) => (
          <option key={each} value={each}>
            {LOCATION_LABELS[each]}
          </option>
        ))}
      </select>

      <label className="location__label" htmlFor={placeId}>
        {copy.place}
      </label>
      <div>
        <input
          id={placeId}
          className="location__input"
          value={draft.place}
          onChange={(event) => c.setDraft({ place: event.target.value })}
          maxLength={200}
          autoComplete="off"
          disabled={saving}
        />
        <p className="location__hint">{copy.placeHelp}</p>
      </div>

      <label className="location__label" htmlFor={countryId}>
        {copy.country}
      </label>
      <div>
        <select
          id={countryId}
          className="location__input"
          value={draft.countryCode}
          onChange={(event) => c.setDraft({ countryCode: event.target.value })}
          disabled={saving}
        >
          <option value="">{copy.countryNone}</option>
          {COUNTRY_OPTIONS.map((each) => (
            <option key={each.code} value={each.code}>
              {each.name}
            </option>
          ))}
        </select>
        <p className="location__hint">{copy.countryHelp}</p>
      </div>

      <label className="location__label" htmlFor={cityId}>
        {copy.city}
      </label>
      <div>
        <input
          id={cityId}
          className="location__input"
          value={draft.city}
          onChange={(event) => c.setDraft({ city: event.target.value })}
          maxLength={120}
          autoComplete="off"
          disabled={saving}
        />
        <p className="location__hint">{copy.cityHelp}</p>
      </div>

      <div className="location__form-actions">
        <button type="submit" className="location__submit" disabled={saving}>
          {saving ? copy.saving : copy.submit}
        </button>
        <button type="button" className="location__cancel" onClick={c.closeForm} disabled={saving}>
          {copy.cancel}
        </button>
      </div>
    </form>
  );
}
